import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Input, 
  Select, 
  Typography, 
  Space,
  Spin,
  message,
  Alert
} from 'antd';
import { UserOutlined, PhoneOutlined, TeamOutlined } from '@ant-design/icons';
import { apiService } from '../../utils/apiService';
import { API_CONFIG } from '../../utils/constants';
import { loadCountriesConfig } from '../../utils/countriesUtils';

const { Title, Text } = Typography;
const { Option } = Select;

/**
 * Driver Step Component
 * Handles driver selection and information
 */
const DriverStep = ({ form, formData, updateFormData, loading }) => {
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);

  /**
   * Load countries configuration on component mount
   */
  useEffect(() => {
    loadCountries();
  }, []);

  /**
   * Load drivers when company is selected
   */
  useEffect(() => {
    if (formData.selectedCompanyId) {
      loadDrivers(formData.selectedCompanyId);
    } else {
      setDrivers([]);
    }
  }, [formData.selectedCompanyId]);

  /**
   * Load countries configuration
   */
  const loadCountries = async () => {
    try {
      setLoadingCountries(true);
      const countries = await loadCountriesConfig();
      setCountryCodes(countries);
    } catch (error) {
      console.error('Error loading countries:', error);
      message.error('Failed to load countries configuration');
    } finally {
      setLoadingCountries(false);
    }
  };

  /**
   * Fetch drivers for the selected company
   */
  const loadDrivers = async (companyId) => {
    setLoadingDrivers(true);
    try {
      const response = await apiService.get(`${API_CONFIG.DRIVERS.GET_COMPANY_DRIVERS}/${companyId}`);
      if (response && Array.isArray(response.drivers)) {
        setDrivers(response.drivers.filter(driver => !driver.deleted && !driver.inactive));
      } else {
        setDrivers([]);
      }
    } catch (error) {
      console.error('Error loading drivers:', error);
      message.error('Failed to load drivers for selected company');
      setDrivers([]);
    } finally {
      setLoadingDrivers(false);
    }
  };

  /**
   * Parse phone number intelligently
   * @param {string} phoneNumber - Raw phone number
   * @returns {Object} - { countryCode, phoneNumber }
   */
  const parsePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return { countryCode: '+27', phoneNumber: '' };
    
    // Remove all spaces and non-numeric characters except + at the start
    const cleaned = phoneNumber.replace(/[^\d+]/g, '');
    
    // Check if it starts with a country code (+)
    if (cleaned.startsWith('+')) {
      // Find where the country code ends (after 2-4 digits)
      const match = cleaned.match(/^(\+\d{2,4})(\d+)$/);
      if (match) {
        return {
          countryCode: match[1],
          phoneNumber: match[2]
        };
      }
    }
    
    // Handle South African numbers starting with 0
    if (cleaned.startsWith('0') && cleaned.length >= 10) {
      return {
        countryCode: '+27',
        phoneNumber: cleaned.substring(1) // Remove leading 0
      };
    }
    
    // Default to South Africa if no country code detected
    return {
      countryCode: '+27',
      phoneNumber: cleaned
    };
  };

  /**
   * Handle driver selection
   */
  const handleDriverSelect = (driverId) => {
    const selectedDriver = drivers.find(d => d.id === driverId);
    
    if (selectedDriver) {
      // Parse the phone number intelligently
      const { countryCode, phoneNumber } = parsePhoneNumber(selectedDriver.mobileNumber);
      const fullPhoneNumber = phoneNumber ? `${countryCode} ${phoneNumber}` : countryCode;
      
      const driverData = {
        selectedDriverId: driverId,
        driverName: selectedDriver.fullName || '',
        driverCell: fullPhoneNumber,
        driverCountryCode: countryCode
      };
      
      updateFormData(driverData);
      
      // Update form fields to ensure validation works
      form.setFieldsValue({
        driverName: selectedDriver.fullName || '',
        driverCell: fullPhoneNumber
      });
    } else {
      const clearData = {
        selectedDriverId: null,
        driverName: '',
        driverCell: '',
        driverCountryCode: '+27'
      };
      
      updateFormData(clearData);
      form.setFieldsValue({
        driverName: '',
        driverCell: ''
      });
    }
  };

  /**
   * Handle manual driver name change
   */
  const handleDriverNameChange = (e) => {
    updateFormData({
      driverName: e.target.value
    });
  };

  /**
   * Handle driver contact number change
   */
  const handleDriverContactChange = (value) => {
    updateFormData({
      driverCell: value
    });
  };

  /**
   * Validate phone number format
   */
  const validatePhoneNumber = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Please enter driver contact number'));
    }
    if (!/^\+\d{1,4}\s?\d{8,15}$/.test(value)) {
      return Promise.reject(new Error('Please enter a valid phone number with country code'));
    }
    return Promise.resolve();
  };

  return (
    <div>
      <Title level={3} style={{ marginBottom: '24px' }}>
        <TeamOutlined style={{ marginRight: '8px' }} />
        Driver Information
      </Title>
      
      <Text type="secondary" style={{ display: 'block', marginBottom: '24px' }}>
        Select a driver from the company or enter driver details manually
      </Text>

      {!formData.selectedCompanyId && (
        <Alert
          message="Company Required"
          description="Please select a customer company in the previous step before selecting a driver."
          type="warning"
          showIcon
          style={{ marginBottom: '24px' }}
        />
      )}

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '16px'
      }}>
        <div style={{ width: '100%' }}>
          <Form.Item
            label="Select Driver (Optional)"
            name="selectedDriverId"
            help="Select a driver from the company, or leave empty to enter details manually"
          >
            <Select
              placeholder="Search and select a driver"
              showSearch
              loading={loadingDrivers}
              disabled={!formData.selectedCompanyId}
              allowClear
              filterOption={(input, option) =>
                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              value={formData.selectedDriverId || undefined}
              onChange={handleDriverSelect}
              notFoundContent={
                !formData.selectedCompanyId 
                  ? 'Please select a company first' 
                  : loadingDrivers 
                    ? <Spin size="small" /> 
                    : 'No drivers found'
              }
            >
              {drivers.map(driver => (
                <Option key={driver.id} value={driver.id}>
                  {driver.fullName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ flex: '1', minWidth: '280px' }}>
            <Form.Item
              label="Driver Name"
              name="driverName"
              rules={[
                { required: true, message: 'Please enter driver name' },
                { min: 2, message: 'Name must be at least 2 characters' }
              ]}
            >
              <Input
                placeholder="Enter driver name"
                prefix={<UserOutlined />}
                value={formData.driverName}
                onChange={handleDriverNameChange}
              />
            </Form.Item>
          </div>
          
          <div style={{ flex: '1', minWidth: '280px' }}>
            <Form.Item
              label="Driver Contact Number"
              name="driverCell"
              rules={[{ validator: validatePhoneNumber }]}
            >
              <Input.Group compact style={{ display: 'flex' }}>
                <Select
                  style={{ flex: '0 0 auto', minWidth: '120px' }}
                  placeholder="Country Code"
                  value={formData.driverCountryCode || '+27'}
                  onChange={(code) => {
                    updateFormData({ driverCountryCode: code });
                    const phoneNumber = formData.driverCell?.split(' ')[1] || '';
                    const fullNumber = phoneNumber ? `${code} ${phoneNumber}` : code;
                    form.setFieldsValue({ driverCell: fullNumber });
                    handleDriverContactChange(fullNumber);
                  }}
                  loading={loadingCountries}
                >
                  {countryCodes.map(item => (
                    <Option key={item.code} value={item.code}>
                      <Space>
                        <span>{item.flag}</span>
                        <span>{item.code}</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
                <Input
                  style={{ flex: '1' }}
                  placeholder="Enter phone number"
                  prefix={<PhoneOutlined />}
                  value={formData.driverCell?.split(' ')[1] || ''}
                  onChange={(e) => {
                    const rawInput = e.target.value;
                    
                    // Apply intelligent parsing if user pastes full number
                    if (rawInput.includes('+') || (rawInput.startsWith('0') && rawInput.length >= 10)) {
                      const { countryCode, phoneNumber } = parsePhoneNumber(rawInput);
                      updateFormData({ driverCountryCode: countryCode });
                      const fullNumber = phoneNumber ? `${countryCode} ${phoneNumber}` : countryCode;
                      form.setFieldsValue({ driverCell: fullNumber });
                      handleDriverContactChange(fullNumber);
                    } else {
                      // Normal input - just update phone number part
                      const code = formData.driverCountryCode || '+27';
                      const fullNumber = rawInput ? `${code} ${rawInput}` : code;
                      form.setFieldsValue({ driverCell: fullNumber });
                      handleDriverContactChange(fullNumber);
                    }
                  }}
                />
              </Input.Group>
            </Form.Item>
          </div>
        </div>
      </div>

      {formData.selectedDriverId && (
        <div style={{ 
          background: '#f6ffed', 
          border: '1px solid #b7eb8f', 
          borderRadius: '6px', 
          padding: '12px',
          marginTop: '16px'
        }}>
          <Text strong style={{ color: '#52c41a', display: 'block' }}>
            Selected Driver:
          </Text>
          <Text>{formData.driverName}</Text>
          <Text type="secondary" style={{ display: 'block', fontSize: '12px' }}>
            Contact: {formData.driverCell}
          </Text>
        </div>
      )}
    </div>
  );
};

export default DriverStep;
